import { useState } from 'react';
import { useToast } from '@/components/ui/use-toast';
import { getApiErrorMessage, isConflict } from './api-result';
import { buildPrimaryChangeFailureNotice, buildPrimaryChangeNotice } from './primary-change-notice';
import {
  useAddSecondaryEmail,
  useChangePrimaryEmail,
  useFindEmailHolder,
  useReleaseAndAddEmail,
  useRemoveSecondaryEmail,
} from './user-emails.hooks';
import type { ManagedUser, Notice, PrimaryEmailCounts, Refresh } from './types';

export type AccountAction =
  | { type: 'change-target' }
  | { type: 'add'; email: string }
  | { type: 'preview'; email: string; counts: PrimaryEmailCounts }
  | { type: 'remove'; email: string }
  | { type: 'release-add'; email: string; holder: ManagedUser };

export interface EmailConflict {
  email: string;
  holder: ManagedUser | null;
}

/**
 * State and handlers for one account card: which panel is open, the add conflict to show, and the
 * write for each panel. Errors surface as toasts; successes are reported through `onChanged`.
 *
 * @param args.user - The account the card shows.
 * @param args.onChanged - Called after a write with the email to search again and the notice to show.
 * @param args.onNotice - Called with a notice to show without searching again, e.g. a failed primary change.
 */
export const useAccountEmailActions = ({
  user,
  onChanged,
  onNotice,
}: {
  user: ManagedUser;
  onChanged: (args: Refresh) => void;
  onNotice: (notice: Notice) => void;
}) => {
  const { toast } = useToast();
  const changePrimary = useChangePrimaryEmail();
  const addSecondaryEmail = useAddSecondaryEmail();
  const removeSecondaryEmail = useRemoveSecondaryEmail();
  const releaseAndAddEmail = useReleaseAndAddEmail();
  const findEmailHolder = useFindEmailHolder();
  const [action, setAction] = useState<AccountAction | null>(null);
  const [conflict, setConflict] = useState<EmailConflict | null>(null);
  const [targetError, setTargetError] = useState<string | null>(null);
  const [resolvingConflict, setResolvingConflict] = useState(false);

  const busy =
    changePrimary.isPending ||
    addSecondaryEmail.isPending ||
    removeSecondaryEmail.isPending ||
    releaseAndAddEmail.isPending ||
    resolvingConflict;

  const fail = (title: string, description: string) =>
    toast({ title, description, variant: 'destructive' });

  const finish = (args: Refresh) => {
    setAction(null);
    setConflict(null);
    onChanged(args);
  };

  const open = (next: AccountAction) => {
    setConflict(null);
    setTargetError(null);
    setAction(next);
  };

  const cancel = () => setAction(null);

  const requestPreview = async ({ email }: { email: string }) => {
    const fromTargetStep = action?.type === 'change-target';
    setConflict(null);
    setTargetError(null);
    try {
      const { counts } = await changePrimary.mutateAsync({
        userId: user.id,
        email,
        oldEmail: user.email,
        keepOldAsSecondary: false,
        dryRun: true,
      });
      setAction({ type: 'preview', email, counts });
    } catch (error) {
      const message = getApiErrorMessage(error, 'Could not preview the change');
      if (fromTargetStep) setTargetError(message);
      else fail('Preview Failed', message);
    }
  };

  const applyPrimaryChange = async ({
    email,
    keepOldAsSecondary,
    reason,
  }: {
    email: string;
    keepOldAsSecondary: boolean;
    reason: string;
  }) => {
    const attempt = { userId: user.id, email, oldEmail: user.email, keepOldAsSecondary, reason };
    try {
      const result = await changePrimary.mutateAsync({ ...attempt, dryRun: false });
      finish({ email, notice: buildPrimaryChangeNotice({ result, attempt }) });
    } catch (error) {
      fail('Change Failed', getApiErrorMessage(error, 'No changes were made'));
      onNotice(buildPrimaryChangeFailureNotice({ error, attempt }));
    }
  };

  const addSecondary = async ({ email, reason }: { email: string; reason: string }) => {
    try {
      await addSecondaryEmail.mutateAsync({ userId: user.id, email, reason });
      finish({
        email: user.email,
        notice: { severity: 'success', message: `Added ${email} as a secondary email.` },
      });
    } catch (error) {
      if (!isConflict(error)) {
        fail('Add Failed', getApiErrorMessage(error, 'No changes were made'));
        return;
      }
      setResolvingConflict(true);
      const holder = await findEmailHolder({ email, excludeUserId: user.id });
      setResolvingConflict(false);
      setAction(null);
      setConflict({ email, holder });
    }
  };

  const removeSecondary = async ({ email, reason }: { email: string; reason: string }) => {
    try {
      await removeSecondaryEmail.mutateAsync({ userId: user.id, email, reason });
      finish({
        email: user.email,
        notice: {
          severity: 'success',
          message: user.isDeleted
            ? `Released ${email} from the deleted account. Another account can use it now.`
            : `Removed ${email}.`,
        },
      });
    } catch (error) {
      setAction(null);
      fail('Remove Failed', getApiErrorMessage(error, 'Check the account for its current emails.'));
    }
  };

  const releaseAndAdd = async ({
    email,
    holder,
    reason,
  }: {
    email: string;
    holder: ManagedUser;
    reason: string;
  }) => {
    try {
      const { addError } = await releaseAndAddEmail.mutateAsync({
        holderId: holder.id,
        userId: user.id,
        email,
        reason,
      });
      if (addError) {
        const message = `${email} was released from the deleted account but could not be added here: ${getApiErrorMessage(addError, 'unknown error')}`;
        fail('Add Failed', message);
        finish({ email: user.email, notice: { severity: 'warning', message } });
        return;
      }
      finish({
        email: user.email,
        notice: {
          severity: 'success',
          message: `Released ${email} from the deleted account and added it here as a secondary email.`,
        },
      });
    } catch (error) {
      setAction(null);
      setConflict(null);
      fail(
        'Release Failed',
        getApiErrorMessage(error, 'Check the account for its current emails.'),
      );
    }
  };

  const previewFor = (email: string) => requestPreview({ email });

  return {
    action,
    conflict,
    busy,
    targetError,
    open,
    cancel,
    previewFor,
    requestPreview,
    applyPrimaryChange,
    addSecondary,
    removeSecondary,
    releaseAndAdd,
  };
};
